import React from 'react';
import { TFunction } from 'react-i18next';
import { DisplayField } from '../components/DisplayForms';
import Dropdown, { DropdownItem } from '../components/Dropdown';
import { PricingType } from '../components/ProductTemplates/types';
import { TableColumnType } from '../components/Table';
import { AddressType, PortfolioBalance, PortfolioBalanceInfo, Size } from '../types/DomainTypes';
import { Product, Region } from '../types/productType';
import { capitalizeFirstLetter, formatterGbp, toInternalId } from '../utils';
import { PasswordChangeValidationKeys } from '../views/Accounts/types';
import defaultWineImage from '../assets/images/defaultWineImage.png';
import { ObjectType } from '../types/commonTypes';
import defaultImage from '../assets/images/default_wine_imageV2.png';

export const getRegions = (t?: TFunction): Region[] => [
  {
    id: 'allregion',
    text: t ? capitalizeFirstLetter(t(`common:regions.allregion`)) : 'All Regions',
    value: 'allregion',
    color: '#1C2450',
  },
  {
    id: 'bordeaux',
    text: t ? capitalizeFirstLetter(t(`common:regions.bordeaux`)) : 'Bordeaux',
    value: 'bordeaux',
    color: '#213F47',
    textColor: 'white',
  },
  {
    id: 'burgundy',
    color: '#EF8E71',
    text: t ? capitalizeFirstLetter(t(`common:regions.burgundy`)) : 'Burgundy',
    value: 'burgundy',
    textColor: 'white',
  },
  {
    id: 'champagne',
    text: t ? capitalizeFirstLetter(t(`common:regions.champagne`)) : 'Champagne',
    color: '#6CB381',
    value: 'champagne',
    textColor: 'white',
  },
  {
    id: 'italy',
    value: 'italy',
    text: t ? capitalizeFirstLetter(t(`common:regions.italy`)) : 'Italy',
    color: '#757587',
    textColor: 'white',
  },
  {
    id: 'rhone',
    value: 'rhone',
    text: t ? capitalizeFirstLetter(t(`common:regions.rhone`)) : 'Rhone',
    color: '#1E234D',
    textColor: 'white',
  },

  {
    value: 'usa',
    id: 'usa',
    text: t ? capitalizeFirstLetter(t(`common:regions.usa`)) : 'USA',
    color: '#E5BE5F',
    textColor: 'white',
  },
  {
    id: 'other',
    value: 'other',
    text: t ? capitalizeFirstLetter(t(`common:regions.other`)) : 'Other',
    color: '#65A3A5',
    textColor: 'white',
  },
  {
    id: 'null',
    value: 'other',
    text: t ? capitalizeFirstLetter(t(`common:regions.other`)) : 'Other',
    color: '#65A3A5',
    textColor: 'white',
  },
];

export const getRegionColors = (selectedRegion?: string) => {
  if (!selectedRegion) {
    return {
      color: '',
      textColor: 'text-black',
    };
  }
  const region = getRegions().find((x) => x.id === toInternalId(selectedRegion.toLowerCase()));

  const color = region?.color;
  const textColor = `text-${region?.textColor || 'white'}`;
  return { color, textColor };
};

export const getInvestMorePricingOptions = (t: TFunction): DropdownItem[] => [
  {
    id: PricingType.MARKET,
    value: PricingType.MARKET,
    text: t('product:wineDetails.investMore.current-market-price'),
    content: (
      <div className="flex justify-between text-base">
        <span>{`${t('product:wineDetails.investMore.current-market-price')}`}</span>
      </div>
    ),
  },
  {
    id: PricingType.CUSTOM,
    value: PricingType.CUSTOM,
    text: t('product:wineDetails.investMore.custom-price-bid-request'),
    content: (
      <div className="flex justify-between text-base">
        <span>{`${t('product:wineDetails.investMore.custom-price-bid-request')}`}</span>
      </div>
    ),
  },
];

export const addressToString = (addressIn: AddressType): string => {
  const address = { ...addressIn };
  delete address.id;
  delete address.isDefault;
  return Object.values(address)
    .filter((x) => x !== '')
    .join(', ');
};

export const getPortfolioDropdownOptions = (portfolioBalances: PortfolioBalance[]) => {
  return portfolioBalances.map((balance) => {
    const { portfolioId, portfolioName, currentHoldings } = balance;
    return {
      id: portfolioId,
      value: portfolioId,
      content: (
        <div className="flex justify-between text-base">
          <span>{portfolioName}</span>
          <span>{`${formatterGbp.format(currentHoldings)}`}</span>
        </div>
      ),
    };
  });
};

export const getPortfolioInfo = (
  portfolioBalances: PortfolioBalance[],
  displayText: Record<string, string>,
  selectedPortfolioBalance: string,
  colors?: string[],
) => {
  if (!portfolioBalances || portfolioBalances.length === 0)
    return {
      selectedText: '',
      info: [],
    };

  const balance = portfolioBalances.find((x) => x.portfolioId === selectedPortfolioBalance);

  // Return empty info if selected portfolio not found in balances
  if (!balance) {
    return {
      selectedText: '',
      info: [],
    };
  }

  const {
    portfolioName,
    inFlow,
    outFlow,
    netPosition,
    currentHoldings,
    valuation,
    balance: availableBalance,
    balancePending,
    capitalInvested,
    transfersIn,
    transfersOut,
    broking,
    refund,
  } = balance;

  const currentHoldingsSeriesColor = (colors || [])[0];
  const netContributionSeriesColor = (colors || [])[1];

  return {
    selectedText: portfolioName,
    info: [
      {
        color: currentHoldingsSeriesColor,
        title: displayText[PortfolioBalanceInfo.CURRENT_ASSETS_TEXT],
        body: (className = 'text-base') => <CurrencyNumber className={`${className}`} value={currentHoldings} />,
        tooltipKey: 'portfolioValuation_tooltip',
        dropdownItems: [
          {
            id: '1',
            value: formatterGbp.format(valuation),
            content: <PortfolioDetails title="Portfolio Value" value={valuation} />,
          },
          {
            id: '2',
            value: formatterGbp.format(availableBalance ?? 0),
            content: <PortfolioDetails title="Available Balance" value={availableBalance} />,
          },
          {
            id: '3',
            value: formatterGbp.format(balancePending ?? 0),
            content: <PortfolioDetails title="Pending Balance" value={balancePending} />,
          },
        ],
      },
      {
        color: currentHoldingsSeriesColor,
        title: displayText[PortfolioBalanceInfo.INFLOWS_TEXT],
        body: (className = 'text-base') => <CurrencyNumber className={`${className}`} value={inFlow} />,
        tooltipKey: 'availableBalance_tooltip',
        dropdownItems: [
          {
            id: '1',
            value: formatterGbp.format(capitalInvested ?? 0),
            content: <PortfolioDetails title="Capital Invested" value={capitalInvested} />,
          },
          {
            id: '2',
            value: formatterGbp.format(Number(transfersIn) + Number(broking)),
            content: <PortfolioDetails title="Transfers In & Broking" value={Number(transfersIn) + Number(broking)} />,
          },
        ],
      },
      {
        color: currentHoldingsSeriesColor,
        title: displayText[PortfolioBalanceInfo.OUTFLOWS_TEXT],
        body: (className = 'text-base') => <CurrencyNumber className={`${className}`} value={outFlow} />,
        tooltipKey: 'balancePending_tooltip',
        dropdownItems: [
          {
            id: '1',
            value: formatterGbp.format(refund ?? 0),
            content: <PortfolioDetails title="Refunds" value={refund} />,
          },
          {
            id: '2',
            value: formatterGbp.format(transfersOut ?? 0),
            content: <PortfolioDetails title="Transfers Out" value={transfersOut} />,
          },
        ],
      },
      {
        color: netContributionSeriesColor,
        title: displayText[PortfolioBalanceInfo.NET_POSITION_TEXT],
        body: (className = 'text-base') => <CurrencyNumber className={`${className}`} value={netPosition} />,
        tooltipKey: 'currentInvested_tooltip',
        dropdownItems: [],
      },

      // {
      //   color: netContributionSeriesColor,
      //   title: displayText[PortfolioBalanceInfo.TOTAL_REFUNDS_TEXT],
      //   body: (className = 'text-base') => <CurrencyNumber className={`${className}`} value={totalRefunds} />,
      //   tooltipKey: 'totalRefunds_tooltip',
      // },
      // Temp hidden net position (https://dev.azure.com/CultWines/Cult%20Wines%20Platform/_workitems/edit/14501)
      // {
      //   title: displayText[PortfolioBalanceInfo.NET_POSITION_TEXT],
      //   tooltipKey: 'netPosition_tooltip',
      //   body: (className = 'text-base') => (
      //     <div className="flex items-center">
      //       <CurrencyNumber
      //         className={`mr-2 ${className} ${netPosition > 0 ? 'text-trendup' : 'text-trenddown'} `}
      //         value={netPosition}
      //       />
      //       ,
      //       <span className={`text-xs ${netPositionPct > 0 ? 'text-trendup' : 'text-trenddown'}`}>{`(${roundNumber(
      //         netPositionPct,
      //       )}%)`}</span>
      //     </div>
      //   ),
      // },
    ],
  };
};

const CurrencyNumber = ({ className, value }: { className: string; value: number | undefined }) => {
  const valueIn = value ?? 0;
  const valueInFomrated = formatterGbp.format(valueIn);
  if (valueIn > 0) {
    return <span className={`${className} text-[#68CA5D]`}>+{valueInFomrated}</span>;
  }

  if (valueIn < 0) {
    return <span className={`${className} text-red`}>{valueInFomrated}</span>;
  }

  return <span className={className}>{valueInFomrated}</span>;
};
const PortfolioDetails = ({
  title,
  value,
  className,
}: {
  title: string;
  value: number | undefined;
  className?: string;
}) => {
  return (
    <div className="py-3 border-b border-b-[#FF906D] dark:border-b-teal-400 flex flex-row item-center gap-1">
      <span className="font-bold text-gray-800 dark:text-gray-200">{title}:</span>
      <span className={`text-gray-700 dark:text-gray-300 ${className || ''}`}>{formatterGbp.format(value || 0)}</span>
    </div>
  );
};

export const getBlankProduct = (): Product => ({
  id: '',
  assetId: 0,
  assetUnitId: '',
  lwin18: '0',
  wineName: '',
  vintage: '',
  region: '',
  dealDate: '',
  dealRef: '',
  dealCCY: '',
  unit: '',
  unitCount: 0,
  qty: 0,
  qtyForSale: 0,
  costPerUnit: 0,
  totalCost: 0,
  valuePerUnit: 0,
  totalValue: 0,
  changedPct: 0,
  totalMgmtFee: 0,
  totalCostWithMgmtFee: 0,
  netPosition: 0,
  profitAndLoss: 0,
  profitAndLossHistory: [],
  priceForSale: 0,
  avgProfitAndLoss: 0,
  latestDealDate: null,
  physicalQuantity: 0,
});

export const getFieldAttributes = (id: string, modelKey: string, label = '') => ({
  id,
  name: modelKey,
  ariaLabel: modelKey,
  modelKey,
  className: '',
  containerClassName: 'mt-5 mr-5',
  translationKey: ``,
  placeholder: '',

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, field?: DisplayField) => null,
  label: {
    text: label,
    isShow: label.length > 0,
    className: 'mb-2 block',
  },
});

const validatePasswordChange = (key: PasswordChangeValidationKeys, value: string) => {
  switch (key) {
    case PasswordChangeValidationKeys.MIN_LENGTH_TEST:
      return value.length > 7;
    case PasswordChangeValidationKeys.CHAR_CASE_TEST:
      return /[A-Z]/.test(value) && /[a-z]/.test(value);
    //  return /(?=.*[a-z])(?=.*[A-Z])/g.test(value);
    case PasswordChangeValidationKeys.DIGIT_TEST:
      return /[\d]/g.test(value);
    case PasswordChangeValidationKeys.SPECIAL_CHAR_TEST:
      // eslint-disable-next-line no-useless-escape
      const specialFormat = /[ `!@#$£%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
      /// [-+_!@#$%^&*.,?]/;
      return specialFormat.test(value);

    default:
      return false;
  }
};

export function getImageUrl(fileName: string, size?: Size): string {
  const imageUrl = process.env.REACT_APP_IMAGE_CDN_URL;

  if (!fileName || fileName.length === 0) return defaultWineImage;
  if (size) {
    if (size.width && !size.height) {
      return `${imageUrl}/width/${size.width}/${fileName}`;
    }
    if (size.height && !size.width) {
      return `${imageUrl}/height/${size.height}/${fileName}`;
    }
    // if both width and height are defined, it returns the image with the given width from the api
  }
  return `${imageUrl}/upload/${fileName}`;
}

export const passwordChangeValidationTests = (
  value: string,
  t: TFunction,
  tPassKeys: string,
): Record<string, { isValid: boolean; text: string }> => {
  const keys = t(`${tPassKeys}`);
  const validations = keys.split(';').reduce((result, key) => {
    return {
      ...result,
      [key]: {
        isValid: validatePasswordChange(key as PasswordChangeValidationKeys, value),
        text: t(`account:settings.${key}`),
      },
    };
  }, {});

  return validations;
};

export const onExportToXlsx = <T,>(data: T[], columns: TableColumnType[] | undefined) => {
  if (!data || data.length === 0) return;
  const dataColumnRefs = (columns || [])
    .filter((z) => z.isVisible === undefined || z.isExportable || z.isVisible)
    .map((x) => x.dataRef);
  return data.map((x) => {
    return dataColumnRefs.reduce((result, dataRef) => {
      const columnRef = (columns || []).find((z) => z.dataRef === dataRef);

      let value = x[dataRef as keyof T] as string | number;
      if (columnRef?.exportFn) {
        value = columnRef?.exportFn(value, x as ObjectType);
      }

      return {
        ...result,
        [`${columnRef?.text}`]: value,
      };
    }, {});
  });
};

// export const onXlsxExport = (data: Product[], columns: TableColumnType[] | undefined) => {
//  if (!data || data.length === 0) return;
//   const dataColumnRefs = (columns || []).map((x) => x.dataRef);
//   return data.map((x) => {
//     return dataColumnRefs.reduce((result, dataRef) => {
//       const columnText = (columns || []).find((z) => z.dataRef === dataRef)?.text;
//       let value = x[dataRef as keyof Product];
//       if (dataRef === DATA_REFS.NAME) value = `${x.vintage} ${value}`;
//       return {
//         ...result,
//         [`${columnText}`]: value,
//       };
//     }, {});
//   });
// };

export const handleImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
  event.currentTarget.src = defaultImage;
};
